import * as React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import JapanNavbar from "../../component/JapanLanding/JapanNavbar";
import JapanFooter from "../../component/JapanLanding/JapanFooter";
import JapanCorporateForm from "../../component/JapanLanding/JapanCorporateForm";
import JapanLanguagesCard from "../../component/JapanLanding/JapanLanguagesCard";
import MetaTags from "../../component/MetaTags";
import { ALL_LANGUAGES } from "../../data/courseDataJapan";

const CORPORATE_FEATURES = [
  {
    icon: "🏢",
    title: "実践的なビジネス英会話",
    description: "業務ですぐに使える実践的な英語力を、少人数制のライブ授業で身につけます。",
  },
  {
    icon: "🌏",
    title: "外国籍社員向け日本語研修",
    description: "職場で必要な日本語力を、レベルに合わせて段階的に習得できます。",
  },
  {
    icon: "📊",
    title: "学習進捗レポート",
    description: "受講状況や上達度を、ご担当者様に定期的にご報告いたします。",
  },
  {
    icon: "🤝",
    title: "専任担当者によるサポート",
    description: "導入から運用まで、専任の担当者が貴社の研修を継続的にサポートします。",
  },
];

const CORPORATE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Corporate Language Training",
  name: "Koderbox 法人向け語学研修",
  description:
    "企業向けの英語・日本語・ドイツ語・フランス語・スペイン語・韓国語・中国語研修。オンラインライブ授業、少人数制、専任担当者によるサポート付き。",
  provider: {
    "@type": "Organization",
    name: "Koderbox",
    sameAs: "https://koderbox.com",
  },
  areaServed: "JP",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "対応言語",
    itemListElement: ALL_LANGUAGES.map((language) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: `${language.label}研修`,
        serviceType: "Corporate Language Training",
      },
    })),
  },
};

class JapanCorporatePage extends React.Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
  }

  scrollToForm = () => {
    if (typeof window !== "undefined" && this.formRef.current) {
      this.formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  render() {
    const pageVariants = {
      initial: { opacity: 0 },
      in: { opacity: 1 },
      out: { opacity: 0 },
    };

    return (
      <>
        <Helmet htmlAttributes={{ lang: "ja" }}>
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="true"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700;800&display=swap"
            rel="stylesheet"
          />
          <link rel="alternate" hrefLang="ja" href="https://koderbox.com/jp/corporate/" />
          <link rel="alternate" hrefLang="x-default" href="https://koderbox.com/" />
        </Helmet>

        <JapanNavbar />
        <motion.div
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
        >
          <main className="jl-page">
            <section id="jl-hero" className="jl-hero">
              <div className="container">
                <div className="jl-hero__grid">
                  <div className="jl-hero__content wow fadeInUp">
                    <span className="jl-hero__badge">法人向け語学研修</span>
                    <h1 className="jl-hero__title">
                      従業員の語学力を
                      <span>法人研修でアップグレード</span>
                    </h1>
                    <p className="jl-hero__subtitle">
                      ビジネス英会話・日本語研修から、ドイツ語・フランス語・スペイン語・韓国語・中国語まで。企業のニーズに合わせた柔軟なプランをご用意しています。
                    </p>
                    <div className="jl-hero__ctas">
                      <button type="button" className="jl-btn jl-btn--primary" onClick={this.scrollToForm}>
                        お問い合わせ・資料請求
                      </button>
                    </div>
                    <div className="jl-hero__trust">
                      <div className="jl-hero__trust-item">
                        <strong>500名以上</strong>
                        <span>研修実績</span>
                      </div>
                      <div className="jl-hero__trust-item">
                        <strong>専任担当者</strong>
                        <span>継続サポート</span>
                      </div>
                      <div className="jl-hero__trust-item">
                        <strong>柔軟</strong>
                        <span>プラン設計</span>
                      </div>
                      <div className="jl-hero__trust-item">
                        <strong>オンライン</strong>
                        <span>対応</span>
                      </div>
                    </div>
                  </div>

                  <div className="jl-hero__visual wow fadeInUp" data-wow-delay="0.2s">
                    <JapanLanguagesCard />
                  </div>
                </div>
              </div>
            </section>

            <section id="jl-languages" className="jl-section jl-languages">
              <div className="container">
                <header className="section-header wow fadeInUp">
                  <span className="jl-section-label">対応言語</span>
                  <h2 className="about_head">
                    7言語の
                    <span className="highlighted-text">法人研修に対応</span>
                  </h2>
                  <p className="jl-section-subtitle">
                    英語・日本語はもちろん、ドイツ語・フランス語・スペイン語・韓国語・中国語まで。貴社が必要とする言語研修をご相談ください。
                  </p>
                </header>

                <div className="jl-languages-grid">
                  {ALL_LANGUAGES.map((language) => (
                    <div className="jl-language-pill" key={language.label}>
                      <span className="jl-language-pill__flag">{language.flag}</span>
                      {language.label}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="jl-why" className="jl-section jl-why">
              <div className="container">
                <header className="section-header wow fadeInUp">
                  <span className="jl-section-label">選ばれる理由</span>
                  <h2 className="about_head">
                    貴社に合わせた
                    <span className="highlighted-text">研修プランをご提案</span>
                  </h2>
                  <p className="jl-section-subtitle">
                    導入企業のニーズに合わせて、柔軟に研修内容を設計します。
                  </p>
                </header>

                <div className="jl-features-grid">
                  {CORPORATE_FEATURES.map((feature, index) => (
                    <div
                      key={feature.title}
                      className="jl-feature-card wow fadeInUp"
                      data-wow-delay={`${index * 0.1}s`}
                    >
                      <div className="jl-feature-card__icon">{feature.icon}</div>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="jl-corporate-form" className="jl-section jl-demo-form" ref={this.formRef}>
              <div className="container">
                <header className="section-header wow fadeInUp">
                  <span className="jl-section-label">お問い合わせ</span>
                  <h2 className="about_head">
                    まずは
                    <span className="highlighted-text">お気軽にご相談ください</span>
                  </h2>
                  <p className="jl-section-subtitle">
                    貴社の課題をお伺いし、最適な研修プランをご提案いたします。
                  </p>
                </header>
                <div className="jl-demo-form__wrapper wow fadeInUp">
                  <JapanCorporateForm />
                </div>
              </div>
            </section>
          </main>

          <JapanFooter />
        </motion.div>

        <MetaTags
          title="法人向け語学研修(英語・日本語・独・仏・西・韓・中)| Koderbox Japan"
          description="Koderboxの法人向け語学研修。英語・日本語・ドイツ語・フランス語・スペイン語・韓国語・中国語をオンラインで。柔軟なプランと専任担当者によるサポート付き。"
          path="/jp/corporate/"
          schema={CORPORATE_SCHEMA}
        />
      </>
    );
  }
}

export default JapanCorporatePage;
